<template>
  <container>
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Feedback</h1>
        <p class="pg-title-sub">Review runner comments by month, trainer, and live session</p>
      </div>
    </div>

    <div class="fb-filter-card">
      <div class="fb-card-header">
        <span class="fb-icon-pill">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
          </svg>
        </span>
        <div>
          <h2 class="fb-card-title">Filters</h2>
          <p class="fb-card-subtitle">Narrow feedback by completed run month and assigned trainer</p>
        </div>
      </div>

      <div class="fb-filter-grid">
        <div class="fb-field">
          <label class="fb-label">Month</label>
          <lv-month-picker v-model="selectedMonth" placeholder="All Time" @selected="handleSelect"></lv-month-picker>
        </div>

        <div class="fb-field">
          <label class="fb-label">Trainer</label>
          <div class="fb-select-wrap">
            <select v-model="trainerId" label="Trainer" name="Trainer" @change="changeHandle" class="fb-trainer-select">
              <option value="All" selected>All trainers</option>
              <option v-for="(trainer, index) in trainerList" v-bind:value="trainer.id" :key="index">{{ trainer.firstName }}</option>
            </select>
            <span class="fb-select-chevron">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="fb-results-card">
      <div class="fb-card-header fb-card-header--split">
        <div class="fb-card-heading">
          <span class="fb-icon-pill">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
            </svg>
          </span>
          <div>
            <h2 class="fb-card-title">Runner Feedback</h2>
            <p class="fb-card-subtitle">Latest comments are sorted by run date with quick links to session and runner details</p>
          </div>
        </div>
        <span class="fb-total-badge">{{ pageTotal || 0 }} total</span>
      </div>

      <div v-if="loading" class="fb-skeleton-table">
        <div class="fb-skeleton-header">
          <div v-for="n in 5" :key="'head-' + n" class="fb-sk-head sk-shimmer"></div>
        </div>
        <div v-for="row in 6" :key="'row-' + row" class="fb-skeleton-row">
          <div class="fb-sk-cell fb-sk-cell--sm sk-shimmer"></div>
          <div class="fb-sk-cell fb-sk-cell--lg sk-shimmer"></div>
          <div class="fb-sk-cell fb-sk-cell--md sk-shimmer"></div>
          <div class="fb-sk-cell fb-sk-cell--md sk-shimmer"></div>
          <div class="fb-sk-cell fb-sk-cell--xl sk-shimmer"></div>
        </div>
      </div>

      <div v-else class="fb-table-wrap">
        <table v-if="tableData.length > 0" class="fb-table">
          <thead class="fb-table-head">
            <tr class="fb-table-head-row">
              <th class="fb-th fb-th--datetime">Date &amp; Time</th>
              <th class="fb-th fb-th--session">Session</th>
              <th class="fb-th fb-th--trainer">Trainer</th>
              <th class="fb-th fb-th--runner">Runner</th>
              <th class="fb-th fb-th--feedback">Feedback</th>
            </tr>
          </thead>
          <tbody class="fb-table-body">
            <tr v-for="feedback in tableData" :key="feedback.id || feedback.raceId + '-' + feedback.runnerId + '-' + feedback.runDateTime" class="fb-table-row">
              <td class="fb-td fb-td--datetime">{{ formatRunDateTime(feedback.runDateTime) }}</td>
              <td class="fb-td fb-td--session">
                <a class="fb-run-link" :href="getRaceLink(feedback.raceId)" target="_blank">{{ feedback.runName }}</a>
              </td>
              <td class="fb-td fb-td--trainer">{{ feedback.trainerName }}</td>
              <td class="fb-td fb-td--runner">
                <a class="fb-runner-link" href="" @click.stop.prevent="runnerData(feedback)">{{ feedback.runnerName }}</a>
              </td>
              <td class="fb-td fb-td--feedback">
                <div class="fb-feedback-text">{{ feedback.text }}</div>
                <a class="fb-translate-link" :href="getTranslateLink(feedback.text)" target="_blank" rel="noopener noreferrer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 8h9"/>
                    <path d="M9 4v4"/>
                    <path d="M7 8c.7 3.6 3.2 6 6 7"/>
                    <path d="M12 8c-.7 2.4-2.2 4.5-4.5 6.2"/>
                    <path d="M16 19l3-7 3 7"/>
                    <path d="M17.1 16h3.8"/>
                  </svg>
                  Translate
                </a>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="tableData.length === 0" class="fb-empty">
          <svg class="fb-empty-icon" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
            <path d="M8 10h8M8 14h5"/>
          </svg>
          <p class="fb-empty-text">No feedback found for the selected filters.</p>
        </div>

        <div v-if="pageTotal" class="fb-pagination">
          <span class="fb-page-total">{{ pageTotal }} total</span>
          <button class="fb-page-btn fb-page-btn--icon" :disabled="pageIndex === 0" @click="pageChange(pageIndex)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <button
            v-for="page in visiblePageNumbers"
            :key="'page-' + page"
            class="fb-page-btn"
            :class="{ 'fb-page-btn--active': pageIndex + 1 === page }"
            @click="pageChange(page)"
          >
            {{ page }}
          </button>
          <button class="fb-page-btn fb-page-btn--icon" :disabled="pageIndex + 1 >= totalPages" @click="pageChange(pageIndex + 2)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <div class="fb-page-size-wrap">
            <select class="fb-page-size" :value="pageSize" @change="pageSizeChange(Number($event.target.value))">
              <option :value="10">10 / page</option>
              <option :value="20">20 / page</option>
              <option :value="50">50 / page</option>
            </select>
            <span class="fb-page-size-chevron">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </span>
          </div>
          <label class="fb-page-jump">
            <span>Go to</span>
            <input class="fb-page-input" type="number" min="1" :max="totalPages" v-model.number="jumpPage" @keyup.enter="jumpToPage" @blur="syncJumpPage">
            <span>page</span>
          </label>
        </div>
      </div>
    </div>
  </container>
</template>

<script>
import Container from "@/components/Container"
import LvMonthPicker from "@/components/Forms/LvMonthPicker"
import FeedbackService from "@/services/feedback"
import TrainerService from "@/services/trainer"
import moment from "moment"

export default {
  name: "feedback",
  data() {
    return {
      tableData: [],
      pageIndex: 0,
      pageTotal: null,
      pageSize: 10,
      selectedMonth: moment(),
      startDate: moment().startOf("month").toISOString(),
      endDate: moment().endOf("month").toISOString(),
      loading: false,
      trainerList: [],
      trainerId: "All",
      jumpPage: 1,
    }
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil((this.pageTotal || 0) / this.pageSize), 1)
    },
    visiblePageNumbers() {
      const pages = []
      const start = Math.max(Math.min(this.pageIndex, this.totalPages - 2), 0) + 1
      const end = Math.min(start + 2, this.totalPages)
      for (let page = start; page <= end; page += 1) {
        pages.push(page)
      }
      return pages
    },
  },
  methods: {
    intialize() {
      TrainerService.getAll().then(response => {
        if (response.data && response.data !== undefined && response.data !== "") {
          this.trainerList = response.data
        }
      }).catch(error => {
        console.log("err--->>", error)
      })
      this.getFeedback(this.startDate, this.endDate, this.trainerId, this.pageIndex, this.pageSize)
    },
    getFeedback(startDate, endDate, trainerId, pageIndex, pageSize) {
      this.loading = true
      FeedbackService.getFeedback(startDate, endDate, trainerId, pageIndex, pageSize).then(response => {
        this.tableData = response.data.content || []
        this.pageTotal = response.data.totalElements
        this.loading = false
      }).catch(error => {
        this.loading = false
        console.log("err--->>", error)
      })
    },
    pageChange(pageIndex) {
      this.pageIndex = pageIndex - 1
      this.jumpPage = pageIndex
      this.getFeedback(this.startDate, this.endDate, this.trainerId, this.pageIndex, this.pageSize)
    },
    pageSizeChange(pageSize) {
      this.pageIndex = 0
      this.pageSize = pageSize
      this.jumpPage = 1
      this.getFeedback(this.startDate, this.endDate, this.trainerId, this.pageIndex, this.pageSize)
    },
    handleSelect(value) {
      this.pageIndex = 0
      this.jumpPage = 1
      if (value != null) {
        const monthYear = moment([value.year(), value.month()])
        this.startDate = moment(monthYear).startOf("month").toISOString()
        this.endDate = moment(monthYear).endOf("month").toISOString()
      } else {
        this.startDate = ""
        this.endDate = ""
      }
      this.getFeedback(this.startDate, this.endDate, this.trainerId, this.pageIndex, this.pageSize)
    },
    changeHandle() {
      this.pageIndex = 0
      this.jumpPage = 1
      this.getFeedback(this.startDate, this.endDate, this.trainerId, this.pageIndex, this.pageSize)
    },
    jumpToPage() {
      const page = Math.min(Math.max(Number(this.jumpPage) || 1, 1), this.totalPages)
      this.pageChange(page)
    },
    syncJumpPage() {
      this.jumpPage = this.pageIndex + 1
    },
    formatRunDateTime(runDateTime) {
      return moment(runDateTime).format("MMM D, YYYY h:mma")
    },
    getRaceLink(raceId) {
      return "#/admin/past-run/detail/" + raceId
    },
    getTranslateLink(text) {
      return "https://translate.google.com/?sl=auto&tl=en&text=" + encodeURIComponent(text || "") + "&op=translate"
    },
    runnerData(feedback) {
      const route = this.$router.resolve({
        name: "runner-data",
        params: {
          runnerId: feedback.runnerId,
        },
      })
      window.open(route.href, "_blank")
    },
  },
  created() {
    this.intialize()
  },
  components: {
    Container,
    LvMonthPicker,
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

</style>

<style scoped>
.fb-filter-card,
.fb-results-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.fb-results-card {
  margin-top: 1.25rem;
}

.fb-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.fb-card-header--split {
  justify-content: space-between;
  gap: 18px;
}

.fb-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.fb-icon-pill {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.fb-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.2;
}

.fb-card-subtitle {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
  line-height: 1.4;
}

.fb-filter-grid {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(180px, 260px);
  gap: 1rem;
  align-items: end;
}

.fb-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.fb-label {
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
}

.fb-select-wrap {
  position: relative;
  min-width: 0;
}

.fb-trainer-select {
  width: 100%;
  height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  padding: 0 36px 0 12px;
  appearance: none;
  outline: none;
}

.fb-trainer-select:focus {
  border-color: #5b4fcf;
  background: #fff;
}

.fb-select-chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8a8a9a;
  pointer-events: none;
}

.fb-total-badge {
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.fb-table-wrap {
  overflow-x: auto;
}

.fb-table {
  width: 100%;
  font-size: 12px;
  border: 1px solid #f0f0f5;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  table-layout: fixed;
  min-width: 960px;
  overflow: hidden;
  background: #fff;
}

.fb-table-head {
  background: #5b4fcf;
  color: #fff;
}

.fb-table .fb-th {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  height: 36px;
  padding: 0 12px;
  text-align: center;
  border: 0;
  border-right: 1px solid #e8e8ed;
  border-bottom: 1px solid #e8e8ed;
  vertical-align: middle;
  background: #5b4fcf;
}

.fb-table .fb-th:first-child {
  border-top-left-radius: 10px;
}

.fb-table .fb-th:last-child {
  border-top-right-radius: 10px;
  border-right: 0;
}

.fb-th--datetime {
  width: 160px;
}

.fb-th--session {
  width: 240px;
}

.fb-th--trainer,
.fb-th--runner {
  width: 118px;
}

.fb-th--feedback {
  width: auto;
}

.fb-table-row:hover {
  background: #fafafa;
}

.fb-td {
  min-height: 70px;
  padding: 14px 12px;
  color: #1a1a2e;
  border: 0;
  border-right: 1px solid #f0f0f5;
  border-bottom: 1px solid #f0f0f5;
  line-height: 1.45;
  text-align: center;
  vertical-align: middle;
  word-break: break-word;
}

.fb-td--datetime {
  white-space: nowrap;
}

.fb-table-row .fb-td:last-child {
  border-right: 0;
}

.fb-table-body .fb-table-row:last-child .fb-td {
  border-bottom: 0;
}

.fb-td--feedback {
  text-align: left;
}

.fb-feedback-text {
  margin-bottom: 8px;
}

.fb-run-link,
.fb-runner-link,
.fb-translate-link {
  color: #5b4fcf;
  font-weight: 600;
  text-decoration: none;
}

.fb-run-link:hover,
.fb-runner-link:hover,
.fb-translate-link:hover {
  color: #4338ca;
  text-decoration: underline;
}

.fb-translate-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  border: 1px solid #e8e8ed;
  border-radius: 7px;
  background: #fff;
  font-size: 11px;
  line-height: 1;
}

.fb-translate-link:hover {
  border-color: #5b4fcf;
  background: #f0effe;
}

.fb-pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 1.25rem;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  flex-wrap: wrap;
}

.fb-page-total {
  color: #1a1a2e;
  margin-right: 2px;
}

.fb-page-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #5b4fcf;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.fb-page-btn:hover:not(:disabled) {
  border-color: #5b4fcf;
  background: #f0effe;
}

.fb-page-btn:disabled {
  color: #8a8a9a;
  background: #fafafa;
  cursor: not-allowed;
}

.fb-page-btn--active,
.fb-page-btn--active:hover {
  border-color: #5b4fcf;
  background: #5b4fcf;
  color: #fff;
}

.fb-page-btn--icon {
  color: #5b4fcf;
}

.fb-page-size-wrap {
  position: relative;
}

.fb-page-size {
  height: 34px;
  min-width: 104px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  padding: 0 32px 0 10px;
  appearance: none;
  outline: none;
}

.fb-page-size:focus {
  border-color: #5b4fcf;
}

.fb-page-size-chevron {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #5b4fcf;
  pointer-events: none;
}

.fb-page-jump {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #1a1a2e;
}

.fb-page-input {
  width: 58px;
  height: 34px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fff;
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  outline: none;
}

.fb-page-input:focus {
  border-color: #5b4fcf;
}

.fb-empty {
  min-height: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #8a8a9a;
  font-size: 13px;
  border: 1px dashed #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
}

.fb-empty-icon {
  color: #c8c8d8;
}

.fb-empty-text {
  margin: 0;
}

.fb-skeleton-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fb-skeleton-header,
.fb-skeleton-row {
  display: grid;
  grid-template-columns: 1.2fr 1.7fr 1fr 1fr 2.8fr;
  gap: 10px;
  align-items: center;
}

.fb-skeleton-header {
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7f7fb;
}

.fb-skeleton-row {
  min-height: 62px;
  padding: 10px 12px;
  border: 1px solid #f0f0f5;
  border-radius: 8px;
}

.fb-sk-head {
  height: 12px;
}

.fb-sk-cell {
  height: 12px;
}

.fb-sk-cell--sm {
  width: 72%;
}

.fb-sk-cell--md {
  width: 80%;
}

.fb-sk-cell--lg {
  width: 88%;
}

.fb-sk-cell--xl {
  width: 96%;
}

@media (max-width: 760px) {
  .fb-filter-card,
  .fb-results-card {
    padding: 1.25rem;
  }

  .fb-filter-grid {
    grid-template-columns: 1fr;
  }

  .fb-card-header--split {
    align-items: flex-start;
  }

  .fb-skeleton-header,
  .fb-skeleton-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// Feedback.vue?67ea0f5f